import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Loader2 } from 'lucide-react';
import { docsApi } from '@/lib/api';
import { useOpenGuide } from '@/lib/useOpenGuide';
import LatestFeatures from '@/components/LatestFeatures';

/**
 * Sidebar "Guides" section — role-appropriate PDF guides for the logged-in user.
 * The list comes from GET /api/docs (authenticated + role-scoped): admins see the Admin +
 * Club Manager guides, facility accounts see the Club Manager guide only.
 *
 * Clicking opens the PDF in a NEW TAB. Because the endpoint needs the Bearer token in the
 * request header (not a URL), we fetch the PDF as a blob and open that blob URL — the browser
 * renders it in-tab if it can read PDFs, otherwise it downloads. We open the blank tab
 * synchronously in the click handler first (so popup blockers allow it), then point it at the blob.
 */
export default function GuideDownloads() {
  const { openGuide, busy, error } = useOpenGuide();

  const { data } = useQuery({
    queryKey: ['docs-guides'],
    queryFn: () => docsApi.list(),
    staleTime: 5 * 60 * 1000,
  });

  const guides = data?.guides ?? [];

  return (
    <div className="border-t border-slate-800 px-3 py-3">
      {guides.length > 0 && (
        <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Guides</p>
      )}
      <ul className="space-y-0.5">
        {guides.map((g) => (
          <li key={g.key}>
            <button
              onClick={() => openGuide(g.key)}
              disabled={busy === g.key}
              title="Opens in a new tab"
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-100 disabled:opacity-50"
            >
              {busy === g.key ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <ExternalLink className="h-4 w-4 shrink-0" />}
              {g.title}
            </button>
          </li>
        ))}
      </ul>
      {error && <p className="mt-1 px-1 text-[11px] text-red-400">{error}</p>}
      {/* Sits with the guides on purpose: the dialog is a summary and points back here. */}
      <div className="mt-1">
        <LatestFeatures />
      </div>
    </div>
  );
}
